import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconColor,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import React from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

import {
  useEmspBoEmspUserContext,
  useEmspBoEmspUserLockStatusContext,
} from '../../../../emsp-bo-emsp-user.context';

export const GeneralCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const emspBoEmspUser = useEmspBoEmspUserContext();
  const emspBoEmspUserlockStatus = useEmspBoEmspUserLockStatusContext();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card direction="column" gap={GapEnum.L}>
      <Stack alignItems="center" gap={GapEnum.XS}>
        <Icon name="user-01" />
        <Text fontSize={TextFontSize.Large} fontWeight={TextWeight.SemiBold}>
          {emspBoEmspUser.firstName} {emspBoEmspUser.lastName}
        </Text>
        {emspBoEmspUser.pseudo && (
          <Text fontSize={TextFontSize.Small} color={TextColor.Secondary}>
            @{emspBoEmspUser.pseudo}
          </Text>
        )}
      </Stack>

      <Divider />

      <Stack direction="column" gap={GapEnum.M}>
        {emspBoEmspUserlockStatus !== null && (
          <Stack direction="column" gap={GapEnum.XS}>
            <MiniTitle>
              {t('delmonicos-shared-sdk.outputs-emsp-user-alt.lock-status')}
            </MiniTitle>
            <Stack alignItems="center" gap={GapEnum.XS}>
              {emspBoEmspUserlockStatus.isLocked ? (
                <Icon name="lock-01" color={IconColor.Error} />
              ) : (
                <Icon name="lock-unlocked-01" color={IconColor.Success} />
              )}
            </Stack>
          </Stack>
        )}
        <Stack direction="column" gap={GapEnum.XS}>
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-emsp-user.email-address')}
          </MiniTitle>
          <Text>{emspBoEmspUser.emailAddress}</Text>
        </Stack>

        <Stack direction="column" gap={GapEnum.XS}>
          <MiniTitle>
            {t(
              'delmonicos-shared-sdk.outputs-emsp-user.is-email-address-verified',
            )}
          </MiniTitle>
          <Stack alignItems="center" gap={GapEnum.XS}>
            {emspBoEmspUser.isEmailAddressVerified ? (
              <Icon name="check-circle" color={IconColor.Success} />
            ) : (
              <Icon name="x-circle" color={IconColor.Error} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" gap={GapEnum.XS}>
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-emsp-user.is-webauthn-enabled')}
          </MiniTitle>
          <Stack alignItems="center" gap={GapEnum.XS}>
            {emspBoEmspUser.isWebauthnEnabled ? (
              <Icon name="check-circle" color={IconColor.Success} />
            ) : (
              <Icon name="x-circle" color={IconColor.Error} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" gap={GapEnum.XS}>
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-emsp-user.last-connection')}
          </MiniTitle>
          <Text>
            {emspBoEmspUser.lastConnection
              ? formatDate(emspBoEmspUser.lastConnection, i18n)
              : '-'}
          </Text>
        </Stack>

        <Stack direction="column" gap={GapEnum.XS}>
          <MiniTitle>
            {t('delmonicos-shared-sdk.outputs-emsp-user.did')}
          </MiniTitle>
          <Text>{emspBoEmspUser.did}</Text>
        </Stack>

        {emspBoEmspUser.billingFrequency && (
          <Stack direction="column" gap={GapEnum.XS}>
            <MiniTitle>
              {t('delmonicos-shared-sdk.outputs-emsp-user.billing-frequency')}
            </MiniTitle>
            <Text>
              {t(
                `delmonicos-shared-sdk.billing-frequency.${emspBoEmspUser.billingFrequency}`,
              )}
            </Text>
          </Stack>
        )}

        {emspBoEmspUser.emspSubscriptionIds &&
          emspBoEmspUser.emspSubscriptionIds.length > 0 && (
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user.emsp-subscription-ids',
                )}
              </MiniTitle>
              {emspBoEmspUser.emspSubscriptionIds.map((id) => (
                <Text key={id}>{id}</Text>
              ))}
            </Stack>
          )}
      </Stack>
    </Card>
  );
};
